/**
 * FWTV Phase 5 admin — Analytics overview.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { BarChart3, Eye, MousePointerClick, Search, Users, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';

const RANGES = [
  { value: 7, label: '7 days' }, { value: 30, label: '30 days' },
  { value: 90, label: '90 days' }, { value: 365, label: '1 year' },
];

export const Analytics = () => {
  const [days, setDays] = useState(7);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['fwtv-analytics', days],
    queryFn: async () => (await api.get(`/fwtv/admin/analytics/overview?days=${days}`)).data,
    refetchInterval: 30000,
  });
  const resetMut = useMutation({
    mutationFn: async () => (await api.delete('/fwtv/admin/analytics/reset')).data,
    onSuccess: () => {
      toast.success('Analytics counters cleared');
      qc.invalidateQueries({ queryKey: ['fwtv-analytics'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const ev = data?.events_by_type || {};
  const ts = data?.timeseries || [];
  const maxTs = Math.max(1, ...ts.map((t) => t.views));

  return (
    <div className="space-y-4" data-testid="fwtv-analytics">
      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-1 text-xs">
          {RANGES.map((r) => (
            <button key={r.value} onClick={() => setDays(r.value)} className={`px-2 py-1 rounded border ${days === r.value ? 'bg-slate-900 text-white' : 'bg-white'}`} data-testid={`fwtv-analytics-range-${r.value}`}>{r.label}</button>
          ))}
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="sm" className="text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid="fwtv-analytics-reset-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Reset analytics
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset all FWTV analytics?</AlertDialogTitle>
              <AlertDialogDescription>
                This clears every event, session, and daily aggregate. Downloaded reports remain, but the KPIs above will all return to zero. Not reversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="fwtv-analytics-reset-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => resetMut.mutate()}
                disabled={resetMut.isPending}
                className="bg-red-600 hover:bg-red-700"
                data-testid="fwtv-analytics-reset-confirm"
              >
                {resetMut.isPending ? 'Resetting…' : 'Yes, reset'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="fwtv-analytics-loading">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading analytics…
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Kpi icon={<Eye className="h-4 w-4" />} label="Total views" value={ev.view || 0} testid="fwtv-analytics-kpi-view" />
        <Kpi icon={<Users className="h-4 w-4" />} label="Unique sessions" value={data?.unique_sessions || 0} testid="fwtv-analytics-kpi-sessions" />
        <Kpi icon={<MousePointerClick className="h-4 w-4" />} label="Clicks" value={ev.click || 0} testid="fwtv-analytics-kpi-clicks" />
        <Kpi icon={<Search className="h-4 w-4" />} label="Searches" value={ev.search || 0} testid="fwtv-analytics-kpi-search" />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Views per day</CardTitle>
          <CardDescription className="text-xs">Client-side page-view events from the embed script — cached to <code>localStorage</code> session IDs so revisits count once.</CardDescription>
        </CardHeader>
        <CardContent>
          {ts.length === 0 ? <p className="text-xs text-slate-400 py-6 text-center">No data yet.</p> : (
            <div className="flex items-end gap-1 h-32" data-testid="fwtv-analytics-timeseries">
              {ts.map((t) => (
                <div key={t.date} className="flex flex-col items-center flex-1" title={`${t.date}: ${t.views} views`}>
                  <div className="w-full bg-indigo-500 rounded-t" style={{ height: `${(t.views / maxTs) * 100}%`, minHeight: '2px' }} />
                  <span className="text-[9px] text-slate-500 mt-1 rotate-[-45deg] origin-top-left whitespace-nowrap">{t.date.slice(5)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <TopList title="Top sermons" rows={data?.top_sermons} testid="fwtv-analytics-top-sermons" />
        <TopList title="Top programmes" rows={data?.top_programmes} testid="fwtv-analytics-top-programmes" />
        <TopList title="Top playlists" rows={data?.top_playlists} testid="fwtv-analytics-top-playlists" />
        <TopList title="Top speakers" rows={data?.top_speakers} testid="fwtv-analytics-top-speakers" />
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Search className="h-4 w-4" /> Top search queries</CardTitle></CardHeader>
        <CardContent>
          {(data?.top_queries || []).length === 0 ? <p className="text-xs text-slate-400 py-4 text-center">No searches in this window.</p> : (
            <ul className="space-y-1 text-xs" data-testid="fwtv-analytics-queries">
              {data.top_queries.map((q) => (
                <li key={q.query} className="flex justify-between border-b py-1">
                  <span className="font-mono">{q.query}</span><Badge variant="outline" className="text-[10px]">{q.count}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-slate-400">
        Public analytics endpoint: <code>POST /api/fwtv/public/analytics</code> — the embed script auto-fires <code>view</code> events on every page with any <code>data-fwtv</code> marker. Custom tracking: <code>window.fwtvTrack(&quot;play&quot;, {'{'} item_type:&quot;sermon&quot;, item_id:&quot;...&quot; {'}'})</code>.
      </p>
    </div>
  );
};

const Kpi = ({ icon, label, value, testid }) => (
  <Card><CardContent className="p-3" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs text-slate-500">{icon}{label}</div>
    <div className="text-2xl font-semibold">{value.toLocaleString()}</div>
  </CardContent></Card>
);

const TopList = ({ title, rows = [], testid }) => (
  <Card><CardHeader className="pb-2"><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
    <CardContent>
      {rows.length === 0 ? <p className="text-xs text-slate-400 text-center py-2">No data.</p> : (
        <ul className="space-y-1 text-xs" data-testid={testid}>
          {rows.map((r) => (
            <li key={r.item_id} className="flex justify-between border-b py-1">
              <span className="truncate max-w-[70%]">{r.title || r.item_id}</span>
              <span className="text-slate-500">{r.views} view{r.views !== 1 && 's'} · {r.unique_sessions} unique</span>
            </li>
          ))}
        </ul>
      )}
    </CardContent>
  </Card>
);

export default Analytics;
